/**
 * 書内検索パネル（FR-SEARCH-01）。
 *
 * リテラル入力（FR-SEARCH-03）→ api.search を叩き、searchDone で届くヒット一覧
 * （ページ＋スニペット・一致を強調）を描く。クリックで該当ページへジャンプし、
 * ビューワが誌面ハイライトを出す。すべて <button>/<input> で作りキーボード操作可。
 */
import { cls } from '../brand';
import type { DisposeRegistry } from '../core/dispose-registry';
import type { SearchHit } from '../core/events';
import type { Translate } from '../i18n/i18n';
import type { ViewerApi } from '../ui/api';
import type { PanelContent } from '../ui/panels';

const DEBOUNCE_MS = 250;

export class SearchPanel implements PanelContent {
  readonly el: HTMLElement;

  private readonly input: HTMLInputElement;
  private readonly status: HTMLParagraphElement;
  private readonly results: HTMLUListElement;
  private readonly unsubscribe: () => void;
  private timer: number | null = null;

  constructor(
    private readonly doc: Document,
    private readonly registry: DisposeRegistry,
    private readonly api: ViewerApi,
    private readonly translate: Translate,
    private readonly minChars: number,
  ) {
    this.el = doc.createElement('div');
    this.el.className = cls('search');

    const form = doc.createElement('div');
    form.className = cls('search-box');
    this.input = doc.createElement('input');
    this.input.type = 'search';
    this.input.className = cls('search-input');
    this.input.autocomplete = 'off';
    // name の無い欄は DevTools が指摘する（id は複数ビューワで衝突するので使わない）。
    this.input.name = 'q';
    this.input.setAttribute('aria-label', translate('search.label'));
    this.input.placeholder = translate('search.placeholder');
    form.appendChild(this.input);

    this.status = doc.createElement('p');
    this.status.className = cls('search-status');
    this.status.setAttribute('aria-live', 'polite');

    this.results = doc.createElement('ul');
    this.results.className = cls('search-results');

    this.el.append(form, this.status, this.results);

    this.registry.listen(this.input, 'input', () => this.onInput());
    this.unsubscribe = this.api.on('searchDone', (payload) => this.onResults(payload));
  }

  /** パネルを開いたら入力へフォーカスする。 */
  activate(): void {
    this.input.focus();
  }

  dispose(): void {
    if (this.timer !== null) this.doc.defaultView?.clearTimeout(this.timer);
    this.unsubscribe();
    this.el.remove();
  }

  private onInput(): void {
    const view = this.doc.defaultView;
    if (this.timer !== null) view?.clearTimeout(this.timer);
    this.timer = view?.setTimeout(() => this.commit(), DEBOUNCE_MS) ?? null;
  }

  private commit(): void {
    const value = this.input.value.trim();
    if (value === '') {
      this.api.clearSearch();
      return;
    }
    if ([...value].length < this.minChars) {
      this.status.textContent = this.translate('search.minChars', { count: this.minChars });
      this.results.replaceChildren();
      return;
    }
    this.status.textContent = this.translate('search.searching');
    this.api.search(value);
  }

  private onResults(payload: { query: string; hits: readonly SearchHit[] }): void {
    if (payload.query === '') {
      this.status.textContent = '';
      this.results.replaceChildren();
      return;
    }
    this.status.textContent =
      payload.hits.length === 0
        ? this.translate('search.noResults')
        : this.translate('search.results', { count: payload.hits.length });

    const fragment = this.doc.createDocumentFragment();
    for (const hit of payload.hits) {
      fragment.appendChild(this.hitItem(hit));
    }
    this.results.replaceChildren(fragment);
  }

  private hitItem(hit: SearchHit): HTMLLIElement {
    const li = this.doc.createElement('li');
    const button = this.doc.createElement('button');
    button.type = 'button';
    button.className = cls('search-hit');

    const page = this.doc.createElement('span');
    page.className = cls('search-hit-page');
    page.textContent = this.translate('search.page', { page: this.api.pageLabel(hit.page) });

    const snippet = this.doc.createElement('span');
    snippet.className = cls('search-hit-snippet');
    this.fillSnippet(snippet, hit.snippet ?? '', hit.matchRanges ?? []);

    button.append(page, snippet);
    this.registry.listen(button, 'click', () => this.api.goto(hit.page));
    li.appendChild(button);
    return li;
  }

  /** スニペットを一致範囲で <mark> に割って描く（matchRanges はコードポイント基準）。 */
  private fillSnippet(
    host: HTMLElement,
    snippet: string,
    ranges: readonly (readonly [number, number])[],
  ): void {
    const chars = [...snippet]; // コードポイント単位で切るため配列化
    const sorted = [...ranges].sort((a, b) => a[0] - b[0]);
    let cursor = 0;
    for (const [start, length] of sorted) {
      if (start < cursor || length <= 0) continue; // 重なり・不正はスキップ
      if (start > cursor) {
        host.appendChild(this.doc.createTextNode(chars.slice(cursor, start).join('')));
      }
      const mark = this.doc.createElement('mark');
      mark.className = cls('search-mark');
      mark.textContent = chars.slice(start, start + length).join('');
      host.appendChild(mark);
      cursor = start + length;
    }
    if (cursor < chars.length) {
      host.appendChild(this.doc.createTextNode(chars.slice(cursor).join('')));
    }
  }
}
